/**
 * DeleteRoleModal - Modale de confirmation de suppression d'un rôle.
 */

import React, { useState } from 'react';
import { CloseIcon, AlertTriangleIcon, TrashIcon } from '../../icons';
import { RoleDetail, API_BASE, authHeaders } from './types';

interface DeleteRoleModalProps {
  role: RoleDetail;
  onClose: () => void;
  onConfirmed: () => void;
}

function DeleteRoleModal({ role, onClose, onConfirmed }: DeleteRoleModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleDelete() {
    setSubmitting(true);
    setError(null);
    try {
      console.log(`[DeleteRoleModal] DELETE /api/roles/${role.id}`);
      const res = await fetch(`${API_BASE}/api/roles/${role.id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json?.error?.message || `Erreur HTTP ${res.status}`);
      }
      console.log('[DeleteRoleModal] Rôle supprimé');
      onConfirmed();
    } catch (err) {
      console.error('[DeleteRoleModal] Erreur delete:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de la suppression');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={modalBackdrop} onClick={onClose}>
      <div style={modalBox} onClick={e => e.stopPropagation()}>
        <div style={modalHeader}>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
            Supprimer le rôle
          </div>
          <button
            type="button"
            onClick={onClose}
            style={modalCloseBtn}
            title="Fermer"
          >
            <CloseIcon size={16} />
          </button>
        </div>

        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{
            fontSize: '14px',
            color: '#1e293b'
          }}>
            Supprimer le rôle <strong>« {role.display_name} »</strong> ?
          </div>

          {role.member_count > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              padding: '12px',
              backgroundColor: '#fff7ed',
              color: '#9a3412',
              border: '1px solid #fdba74',
              borderRadius: '6px',
              fontSize: '13px'
            }}>
              <AlertTriangleIcon size={16} color="#c2410c" />
              <div>
                {role.member_count} utilisateur{role.member_count > 1 ? 's' : ''} {role.member_count > 1 ? 'ont' : 'a'} ce rôle.
                {' '}
                {role.member_count > 1 ? 'Ils seront' : 'Il sera'} automatiquement basculé{role.member_count > 1 ? 's' : ''} sur le rôle par défaut (Technicien).
              </div>
            </div>
          )}

          <div style={{
            fontSize: '13px',
            color: '#64748b'
          }}>
            Cette action est irréversible.
          </div>

          {error && (
            <div style={{
              padding: '10px 12px',
              backgroundColor: '#fef2f2',
              color: '#991b1b',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              fontSize: '13px'
            }}>
              {error}
            </div>
          )}
        </div>

        <div style={modalFooter}>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f1f5f9',
              color: '#475569',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              cursor: submitting ? 'not-allowed' : 'pointer'
            }}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={submitting}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              backgroundColor: submitting ? '#fca5a5' : '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: submitting ? 'not-allowed' : 'pointer'
            }}
          >
            <TrashIcon size={14} color="white" />
            {submitting ? 'Suppression...' : 'Supprimer'}
          </button>
        </div>
      </div>
    </div>
  );
}

const modalBackdrop: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(15, 23, 42, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1100,
  padding: '20px'
};

const modalBox: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
  maxWidth: '480px',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden'
};

const modalHeader: React.CSSProperties = {
  padding: '14px 20px',
  borderBottom: '1px solid #e2e8f0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
};

const modalFooter: React.CSSProperties = {
  padding: '12px 20px',
  borderTop: '1px solid #e2e8f0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '8px',
  backgroundColor: '#f8fafc'
};

const modalCloseBtn: React.CSSProperties = {
  width: '28px',
  height: '28px',
  border: 'none',
  background: 'transparent',
  color: '#64748b',
  cursor: 'pointer',
  borderRadius: '6px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

export default DeleteRoleModal;
